import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useI18n } from '../i18n';
import { APP_VERSION, PLATFORM, displayable } from '../lib/config';
import { deviceInfo } from '../lib/deviceInfo';
import { maskKey } from '../lib/deviceKey';
import { ipOf } from '../lib/ipInfo';
import { networkTitleKey } from '../lib/networkStatus';
import type { NetStatus } from '../lib/networkStatus';
import type { DeviceInfo, MeasurementContext, RunConfig } from '../lib/types';
import type { CheckLoop } from '../hooks/useCheckLoop';
import { AlertDialog, BusyDialog } from './ui/Dialog';
import { Icon } from './ui/Icon';
import { LabelValueRow, Module, ModuleDivider, ModuleLabel } from './ui/Module';
import { NeonDangerButton } from './ui/NeonButton';
import { NoteRow } from './ui/NoteRow';
import { ReadOnlyRow } from './ui/Controls';
import { NoInternetBanner } from './ui/NetworkStatusUi';
import { ScreenContent, TopAppBar } from './ui/ScreenScaffold';

/**
 * Settings — the port of `ui/settings/SettingsScreen.kt`.
 *
 * This is the screen where the two platforms diverge most, so the rule for
 * reading it is: **every block below names the Android control it stands in for
 * and the platform fact that made the substitution necessary.** There are no
 * silent gaps, and nothing here fakes a capability Safari does not have.
 *
 * Android's order is kept exactly — language, operation, device setup, device
 * facts, device key — because the two apps are used side by side in the field by
 * the same people, and a section that has moved is a section they have to hunt
 * for on a phone they are holding at arm's length.
 *
 * That order is also the file's own table of contents. The screen is a stack of
 * independent sections that share nothing but their order, so each is a function
 * below and the explanation of why it looks the way it does sits on that
 * function rather than floating in the middle of one long `return`:
 *
 * 1. {@link LanguageSection} — the only writable setting on the screen.
 * 2. {@link OperationSection} — what the server told this device to run.
 * 3. {@link DeviceSetupSection} — the instructions Safari cannot carry out.
 * 4. {@link DeviceFactsSection} — what the device reports about itself.
 * 5. {@link CaveatPanel} — SPEC §2, between the facts and the key.
 * 6. {@link DeviceKeySection} — the masked key and the way out of service.
 *
 * The three substitutions, in one place:
 *
 * 1. The auto-run **switch** and the interval **chips** become read-only rows.
 *    This app has no settings of its own and always follows the newest server
 *    value (`ios-webclip/CLAUDE.md`, SPEC §9).
 * 2. The battery-optimisation button, the OEM autostart button and the whole
 *    Permissions module become a **checklist of instructions**. Safari can
 *    neither read nor change any of those settings.
 * 3. The Android-version row is **dropped entirely** — SPEC §8.
 */

/**
 * How long the busy dialog will wait for a running cycle to actually stop
 * before returning to the gate anyway.
 *
 * Android's `SettingsRoute` awaits its ViewModel's teardown with no ceiling,
 * and it can: the thing it is waiting on is its own coroutine. Here the thing
 * being waited on is an in-flight `fetch`, and an abort is only observed when
 * the request it is attached to unwinds — which, against our own backend, can
 * be `API_TIMEOUT_MS` away. Removing the key must not be hostage to a network
 * round trip nobody can cancel from the outside, and this dialog cannot be
 * dismissed by hand. On expiry the app returns to the gate regardless; the
 * abandoned cycle cannot submit under the old key either way, because the shell
 * remounts the session and the runner's own signal is already aborted.
 */
const REMOVAL_WAIT_CEILING_MS = 5_000;

export function SettingsScreen({
  deviceKey,
  loop,
  status,
  onOpenLanguage,
  onKeyRemoved,
}: {
  deviceKey: string;
  loop: CheckLoop;
  status: NetStatus;
  onOpenLanguage(): void;
  onKeyRemoved(): void;
}): JSX.Element {
  const { t } = useI18n();

  // Reads `navigator` once. Every field is a constant for the life of the tab,
  // and this component re-renders on every phase change of a running cycle.
  const info = useMemo(() => deviceInfo(), []);

  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const running = loop.running;

  /**
   * The busy dialog closes when the cycle is genuinely down, not on a timer
   * chosen to look plausible.
   *
   * This is Android's rule — `viewModel.removeDeviceKey(onKeyRemoved)`
   * navigates only after the teardown completes, so the gate never appears
   * while a run is still submitting under the old key. The equivalent signal
   * here is `loop.running` going false after `cancel()` aborted the cycle's
   * `AbortController`. If no cycle was running the condition is already true
   * and the exit is immediate, which is correct: there was nothing to tear down.
   */
  useEffect(() => {
    if (!removing) return;
    if (!running) {
      onKeyRemoved();
      return;
    }
    const timer = setTimeout(onKeyRemoved, REMOVAL_WAIT_CEILING_MS);
    return () => clearTimeout(timer);
  }, [removing, running, onKeyRemoved]);

  return (
    <>
      <TopAppBar title={t('nav_settings')} />
      <ScreenContent>
        <NoInternetBanner status={status} />
        <LanguageSection onOpen={onOpenLanguage} />
        <OperationSection config={loop.config} />
        <DeviceSetupSection />
        <DeviceFactsSection info={info} context={loop.context} status={status} />
        <CaveatPanel />
        <DeviceKeySection
          deviceKey={deviceKey}
          removing={removing}
          onRemove={() => setConfirmRemove(true)}
        />
      </ScreenContent>

      {confirmRemove ? (
        /*
         * The confirmation keeps Android's exact shape — icon, title, a body
         * that spells out the consequences, cancel on the left in the neutral
         * colour and the destructive action on the right.
         *
         * The body text is `device_key_remove_body` unchanged, and one line of
         * it is worth flagging: it says the key stored on this phone will be
         * deleted. **There is no store here.** The key lives in the WebClip
         * icon's URL (SPEC §13), so "removing" it only drops the in-memory
         * session and returns the app to the key-entry gate; the icon still
         * carries the old key and reopening it re-provisions the device
         * unchanged. The consequences the string describes — scheduled checks
         * stop, and the key is needed again to set the device up — are true on
         * both platforms, which is why the shared string still fits. The
         * mechanism underneath is the part that differs, and this comment is
         * where it is written down rather than in a fifth translation of a
         * string that would then have to be maintained in four languages.
         */
        <AlertDialog
          icon="key-off"
          title={t('device_key_remove_title')}
          body={t('device_key_remove_body')}
          confirmText={t('device_key_remove_action')}
          onConfirm={() => {
            setConfirmRemove(false);
            setRemoving(true);
            // Stops a cycle that may be mid-submit. Without this the run
            // carries on under a key the operator has just retired and posts
            // one more batch after the screen said it had stopped.
            loop.cancel();
          }}
          cancelText={t('common_cancel')}
          onDismiss={() => setConfirmRemove(false)}
          destructive
        />
      ) : null}

      {/* Not dismissible, and not instant: the teardown waits for a run that
       * may be mid-submit to actually be down. Without a dialog the screen
       * would simply sit there for a second or two and invite a second tap. */}
      {removing ? (
        <BusyDialog title={t('device_key_removing')} body={t('device_key_removing_body')} />
      ) : null}
    </>
  );
}

/**
 * Language — one control, not two.
 *
 * Android's comment applies unchanged: this used to be a read-only row followed
 * by a separate "change language" button, which is two affordances for one
 * setting, and the one that looked tappable was not.
 */
function LanguageSection({ onOpen }: { onOpen(): void }): JSX.Element {
  const { t, locale } = useI18n();
  return (
    <>
      <ModuleLabel text={t('settings_section_language')} />
      <Module>
        <button type="button" className="module__row module__row--clickable" onClick={onOpen}>
          <Icon name="language" className="c-accent" />
          <span className="module__row-title t-body-large">{t('settings_language')}</span>
          {/* Always written in its own script, exactly as on Android, so that
           * someone who cannot read the language the app is currently in can
           * still find their own. */}
          <span className="module__row-value t-body-medium c-accent clamp-2">
            {locale.nativeName}
          </span>
          <Icon name="chevron-right" className="module__chevron" />
        </button>
      </Module>
    </>
  );
}

/**
 * Operation — **replaces Android's auto-run `Switch` and its `FilterChip` row of
 * intervals**, and deliberately does not reproduce them as disabled controls.
 *
 * This app has no settings of its own: every cycle re-reads
 * `GET /device-api/config` and follows whatever came back (SPEC §9). Android
 * needs a config-conflict rule because it has hand-set values to protect; there
 * are none here, so there is no rule to get wrong and nothing for a switch to
 * toggle. A greyed-out switch would still say "there is a setting here you are
 * not allowed to touch", which is a different and untrue statement.
 *
 * The numbers shown are the ones the device will actually run on. `clampConfig`
 * applies `MIN_CHECK_INTERVAL_MINUTES` on the way in, so what is on screen and
 * what the scheduler uses cannot drift — the failure Android's
 * `INTERVAL_OPTIONS` comment describes ("the user picks 10, the screen says 10,
 * the device runs 15") is closed here by clamping before display rather than
 * after it.
 */
function OperationSection({ config }: { config: RunConfig }): JSX.Element {
  const { t, n } = useI18n();
  return (
    <>
      <ModuleLabel text={t('ios_settings_section_operation')} />
      <Module>
        <ReadOnlyRow
          label={t('ios_settings_schedule')}
          value={
            config.fromServer
              ? t('ios_settings_schedule_value', n(config.checkIntervalMinutes))
              : t('ios_settings_schedule_default', n(config.checkIntervalMinutes))
          }
        />
        <ModuleDivider />
        {/* Rounds and the gap between them define **the measurement**, not a
         * preference — every device in the fleet has to agree on them or the
         * data stops being comparable. Read-only on Android too, for exactly
         * this reason, and shown because it is the first thing anyone wants
         * to see when one device reports oddly. */}
        <ReadOnlyRow
          label={t('settings_measurement')}
          value={
            config.fromServer
              ? t(
                  'settings_measurement_value',
                  n(config.checkRounds),
                  n(config.roundIntervalSeconds),
                )
              : t('settings_measurement_unknown')
          }
        />
        {config.autoCheckEnabled ? null : (
          <>
            <ModuleDivider />
            {/* `autoCheckEnabled: false` does not tear the loop down (SPEC
             * §9, API.md): the device keeps waking and keeps asking, which is
             * the only way the server can ever switch it back on. Worth
             * saying on screen, or a stopped device looks broken to whoever
             * is holding it. */}
            <NoteRow
              icon="info"
              text={t('ios_settings_autorun_off')}
              size={18}
              tint="c-warn"
              textClassName="t-body-small"
            />
          </>
        )}
      </Module>
      <p className="caption t-body-small">{t('ios_settings_foreground_only')}</p>
    </>
  );
}

/**
 * Required device setup — **replaces Android's "ignore battery optimisation"
 * button, its OEM autostart button and its entire Permissions module.**
 *
 * The problems those solve are real on iOS too — a phone that sleeps or
 * throttles stops reporting — but Safari cannot read, request or open any of
 * these settings, and there is no permission model to query. So they are
 * instructions to a human with an `info` icon, **not** granted/denied rows.
 * Rendering a verdict we have no way to measure is precisely the failure mode
 * this codebase exists to avoid: it would look exactly like Android's
 * Permissions module and mean nothing.
 *
 * Which is also why every row is an `info` icon in `--warn` and never a check or
 * a cross. The tempting version is Android's `PermissionRow` — a tick, a label,
 * and the word "granted" — and it is unbuildable here for a reason that is not a
 * missing API: Safari has no way to observe Auto-Lock, Low Power Mode, the
 * charger or Guided Access at all.
 */
function DeviceSetupSection(): JSX.Element {
  const { t } = useI18n();
  // Full contrast, not `.note-row__text`'s secondary: these are not a caption
  // explaining something, they are the content of the module — steps a person
  // carries out on the phone in their hand, often in daylight.
  const textClassName = 'settings-setup__text t-body-medium';
  return (
    <>
      <ModuleLabel text={t('ios_settings_section_device_setup')} />
      <Module>
        <NoteRow
          icon="info"
          text={t('ios_setup_autolock')}
          tint="c-warn"
          textClassName={textClassName}
        />
        <ModuleDivider />
        <NoteRow
          icon="info"
          text={t('ios_setup_lowpower')}
          tint="c-warn"
          textClassName={textClassName}
        />
        <ModuleDivider />
        <NoteRow
          icon="info"
          text={t('ios_setup_charger')}
          tint="c-warn"
          textClassName={textClassName}
        />
        <ModuleDivider />
        <NoteRow
          icon="info"
          text={t('ios_setup_guided_access')}
          tint="c-warn"
          textClassName={textClassName}
        />
      </Module>
      <p className="caption t-body-small">{t('ios_setup_note')}</p>
    </>
  );
}

/**
 * Device — what this handset reports about itself.
 *
 * One inset for the whole block rather than per row, matching Android: padding
 * each row individually put the device facts on a different left edge from every
 * sibling module and made the block read as ragged.
 *
 * The three values that come off the cycle's `MeasurementContext` are printed
 * through `displayable` (`lib/config.ts`, and `ipOf` applies it for the IP).
 * That is where the rule and the reason for it are written down: `UNKNOWN` is a
 * wire value the backend needs, and it is English, so the UI's spelling of the
 * same idea is `value_unknown`.
 */
function DeviceFactsSection({
  info,
  context,
  status,
}: {
  info: DeviceInfo;
  context: MeasurementContext | null;
  status: NetStatus;
}): JSX.Element {
  const { t } = useI18n();
  const unknown = t('value_unknown');
  return (
    <>
      <ModuleLabel text={t('settings_section_device')} />
      <Module>
        <div className="settings-device-list">
          {/* Device *class* only — `navigator.userAgent` says "iPhone" on
           * every iPhone ever made, and there is no honest way to infer the
           * generation (SPEC §14 #4). Generic and true beats specific and
           * invented. */}
          <LabelValueRow label={t('device_model')} value={info.model} />
          <ModuleDivider />
          {/*
           * **No `device_os` row.** Android shows the OS version and SDK
           * level; the equivalent here would mean scraping "OS 17_2 like Mac
           * OS X" out of the UA string, which is not a format Apple has
           * promised to keep (SPEC §8). A best-effort value that silently
           * becomes wrong is worse than no value, so the row is dropped
           * rather than filled with a guess. The raw user agent is printed at
           * the bottom of this list instead — it is the honest version of the
           * same information.
           */}
          <LabelValueRow label={t('ios_device_platform')} value={PLATFORM} />
          <ModuleDivider />
          <LabelValueRow label={t('device_app_version')} value={APP_VERSION} />
          <ModuleDivider />
          <LabelValueRow
            label={t('device_network')}
            value={t(networkTitleKey(status))}
            className={status.online ? undefined : 'c-error'}
          />
          <ModuleDivider />
          {/* Carrier and region come from ipwho.is — the real egress IP —
           * never from the SIM, same as Android. They are read off the
           * cycle's `MeasurementContext`, which snapshotted them when the run
           * started, so this describes the network the batch on screen was
           * measured on rather than the one the phone happens to be on now. */}
          <LabelValueRow
            label={t('device_carrier')}
            value={displayable(context?.carrier) ?? unknown}
          />
          <ModuleDivider />
          <LabelValueRow
            label={t('device_region')}
            value={displayable(context?.region) ?? unknown}
          />
          <ModuleDivider />
          <LabelValueRow label={t('device_ip')} value={ipOf(context?.ipInfo) ?? unknown} />
          <ModuleDivider />
          <LabelValueRow
            label={t('ios_device_user_agent')}
            value={displayable(info.meta.userAgent) ?? unknown}
          />
        </div>
      </Module>
    </>
  );
}

/**
 * The caveat — SPEC §2, on screen rather than only in the docs, and the single
 * most important sentence in the app: a healthy verdict from this platform means
 * *reachable*, not *verified alive*. A `no-cors` probe reports no status code,
 * so an ISP that blocks by DNS-redirecting to a notice page produces a real
 * response this app cannot tell from the real site.
 *
 * It gets a warn accent and full-contrast text rather than the `.caption`
 * treatment the other explanations use, because the person who needs to read it
 * is the one about to compare these numbers with the Android fleet's — and a
 * footnote is what they will skip.
 */
function CaveatPanel(): JSX.Element {
  const { t } = useI18n();
  return (
    <Module className="settings-caveat">
      <NoteRow icon="info" text={t('ios_caveat')} tint="c-warn" />
    </Module>
  );
}

/**
 * The device key, and the one control that takes this device out of service.
 *
 * Last on the screen on purpose, exactly as on Android: it should not sit next
 * to anything you might tap by reflex.
 */
function DeviceKeySection({
  deviceKey,
  removing,
  onRemove,
}: {
  deviceKey: string;
  removing: boolean;
  onRemove(): void;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <>
      <ModuleLabel text={t('settings_section_device_key')} />
      <Module>
        {/* Masked, always. The full key is a credential — it is what
         * `X-Device-Key` carries — and a screen anybody can photograph is not
         * where it goes. Four characters either side is enough to tell two
         * provisioned devices apart over the phone and not enough to reuse. */}
        <div className="settings-key">
          <code className="settings-key__value mono t-body-medium c-accent">
            {maskKey(deviceKey)}
          </code>
        </div>
      </Module>
      <NeonDangerButton
        text={t('device_key_remove')}
        icon="key-off"
        onClick={onRemove}
        disabled={removing}
      />
    </>
  );
}
